// React
import {useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';

// FullCalendar Imports
import FullCalendar from '@fullcalendar/react';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';

// Store
import useSiteStore from '../../store/siteStore.js';
import useProfileStore from '../../store/profileStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Chip, Stack, Menu, MenuItem, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { Close as CloseIcon, KeyboardArrowDown, GridView as GridViewIcon } from '@mui/icons-material';

// Style
import { defaultColorScheme, tealColorScheme} from '../../styles/colorSchemes/index.js';
import './style2.css';

// Other
import { formatTrainingFocus, getWeek } from '../../utils/index.js';
import { getWeeklyScheduleOfAthlete } from '../../service/httpService/weeklySchedule/index.js';
import { getAllFeedbacksOfWeekOfAthlete } from '../../service/httpService/feedback/index.js';

// Lazy-load Plotly
const Plot = lazy(async () => {
    const factory = await import('react-plotly.js/factory.js');
    const Plotly = await import('plotly.js/lib/core.js');

    // import trace modules and register them with the Plotly core instance
    const [bar, scatter, pie] = await Promise.all([
        import('plotly.js/lib/bar.js'),
        import('plotly.js/lib/scatter.js'),
        import('plotly.js/lib/pie.js'),
    ]);

    // register traces so they are available to Plotly
    if (Plotly && typeof Plotly.register === 'function') {
        Plotly.register([bar, scatter, pie]);
    }

    return { default: factory.default(Plotly) };
});

class TrainingSessionData{
    constructor(startDate, endDate, focus, description, duration, intensity, color, hasFeedback, feedback){
        this.startDate = startDate;
        this.endDate = endDate;
        this.focus = focus;
        this.description = description;
        this.duration = duration;
        this.intensity = intensity;
        this.color = color; 
        this.hasFeedback = hasFeedback;
        this.feedback = feedback;
    }
};
class FeedbackData{
    constructor(actualDuration=0, actualIntensity=0, physicalExhaustion=-2, mentalExhaustion=-2, comment="", hasNewFiles=false){
        this.actualDuration = actualDuration;
        this.actualIntensity = actualIntensity;
        this.physicalExhaustion = physicalExhaustion;
        this.mentalExhaustion = mentalExhaustion;
        this.comment = comment;
        this.hasNewFiles = hasNewFiles;
    }
};
    
const getColorFromExhaustion = (exhaustion, border) => {
    let ret = "";
    switch (exhaustion) {
        case -2 : ret = "rgba(0,0,0,0)"; break;
        case -1 : ret ="lightgrey"; break;
        case 0: ret = "grey"; break;
        case 1: ret = "green"; break;
        case 2: ret = "yellow"; break;
        case 3: ret = "red"; break;
        default: ret = "green"; break;
    }
    if( !border ) return ret;
    return ret === "yellow" ? "grey" : ret;
};

function useWindowWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return width;
}

const YearCalendar = (props) =>{ 
    const { colorScheme } = useProfileStore();
    const { isDarkMode } = useAppTheme();
    let cs = defaultColorScheme
    if (colorScheme === 'teal') {
        cs = tealColorScheme;
    }    
    
    let colors = cs.trainingFocusColor;
    let colors2 = cs.analysisDurationColor;
    let colors3 = cs.analysisIntensityColor;

    const { currentYear, prepCreateWeeklySchedule, goToWeeklySchedule, selectedAthlete, onYearChange, refreshTrigger = 0 } = props;

    const { selectedCalenderWeek } = useSiteStore();
    
    // Responsive Check
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 601;
    const isTablet = windowWidth >= 601 && windowWidth <= 1000;
    const isDesktop = windowWidth > 1000;
    const isTouchDevice = isMobile || isTablet;

    const [showPlots, setShowPlots] = useState(JSON.parse(localStorage.getItem('showPlots')) || false);
    const [calendarDataSource, setCalendarDataSource] = useState([]); 
    const [calendarEvents, setCalendarEvents] = useState([]); 
    
    const [selectedDateRange, setSelectedDateRange] = useState({start : new Date(), end : new Date()});    
    const [plotCurrentFocus, setPlotCurrentFocus] = useState( 0 );
    const [plotData, setPlotData]  = useState( [] );
    const [plotLayout, setPlotLayout] = useState({});
    const [yPlotData, setYPlotData] = useState( [] );
    const [plotRevision, setPlotRevision] = useState( 0 );
    const [plotPieChart, setPlotPieChart] = useState( false );
    const [plotDataPieChart, setPlotDataPieChart] = useState( [] );
    const [plotLayoutPieChart, setPlotLayoutPieChart] = useState( [] );
    const [plotDataBarChart, setPlotDataBarChart] = useState( {} );
    const [plotDataBarChart2, setPlotDataBarChart2] = useState( {} );
    const [plotLayoutBarChart, setPlotLayoutBarChart] = useState( [] );
    const [plotExhaustionColors, setPlotExhaustionColors] = useState( [] );
    const [pressedKey, setPressedKey] = useState("none");
    const [plotPieBarChartTitle, setPlotPieBarChartTitle] = useState("");
    
    // Custom legend state
    const [legendMenuAnchor, setLegendMenuAnchor] = useState({ dauer: null, intensitaet: null, rueckmeldung: null, load: null, erschoepfung: null });
    const [legendVisibility, setLegendVisibility] = useState({
        dauer: true,
        dauerKum: true,
        intensitaet: true,
        intensitaetKum: true,
        rueckmeldungDauer: true,
        rueckmeldungIntensitaet: true,
        load: false,
        loadKum: false,
        rueckmeldungLoad: false,
        trainingLoadKum: false,
        physischeErschoepfung: false,
        mentaleErschoepfung: false,
    });

    // Month column selector state (stores number of columns directly)
    const [monthColumnCount, setMonthColumnCount] = useState(
        () => parseInt(localStorage.getItem('yearCalendarMonthColumns') || '4')
    );
    const [rowSelectorAnchor, setRowSelectorAnchor] = useState(null);

    // Handle column count change
    const handleColumnCountChange = (count) => {
        setMonthColumnCount(count);
        localStorage.setItem('yearCalendarMonthColumns', count.toString());
        setRowSelectorAnchor(null);
    };

    // Calculate columns based on screen size (only desktop >1000px uses custom columns)
    const monthColumns = isDesktop ? monthColumnCount : 1;
    // Mobile touch selection states
    const [touchStartCell, setTouchStartCell] = useState(null);
    const [touchCurrentCell, setTouchCurrentCell] = useState(null);
    const [isTouchSelecting, setIsTouchSelecting] = useState(false);
    const touchTimerRef = useRef(null);
    const LONG_PRESS_DELAY = 300; // ms before long-press activates

    const calendarRef = useRef(null);
    
    // Ref to always have the latest goToWeeklySchedule function in event handlers
    const goToWeeklyScheduleRef = useRef(goToWeeklySchedule);
    useEffect(() => {
        goToWeeklyScheduleRef.current = goToWeeklySchedule;
    }, [goToWeeklySchedule]);

    // Cleanup any lingering popups on unmount or when events change
    useEffect(() => {
        return () => {
            // Remove all event-popup elements from the DOM
            const popups = document.querySelectorAll('.event-popup');
            popups.forEach(popup => {
                if (popup.parentNode) {
                    popup.parentNode.removeChild(popup);
                }
            });
        };
    }, [calendarEvents]);

    // Apply colored dots to day cells when calendar events change
    useEffect(() => {
        //if (calendarEvents.length === 0) return;
        
        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            // Get all day cells
            const dayCells = document.querySelectorAll('.fc-daygrid-day');
            
            dayCells.forEach(dayCell => {
                // Skip grayed-out dates (dates outside the current year/month)
                if (dayCell.classList.contains('fc-day-other')) return;
                
                const dateStr = dayCell.getAttribute('data-date');
                if (!dateStr) return;
                
                // Find events for this date (use local date to avoid timezone issues)
                const eventsForDay = calendarEvents.filter(event => {
                    if (!event.start) return false;
                    const eventStart = new Date(event.start);
                    // Format as YYYY-MM-DD using local time
                    const eventDate = `${eventStart.getFullYear()}-${String(eventStart.getMonth() + 1).padStart(2, '0')}-${String(eventStart.getDate()).padStart(2, '0')}`;
                    return eventDate === dateStr;
                });

                const dayFrame = dayCell.querySelector('.fc-daygrid-day-frame');
                if (!dayFrame) return;
                
                // Remove existing dots wrapper/container
                const existingWrapper = dayFrame.querySelector('.event-dots-wrapper');
                if (existingWrapper) {
                    existingWrapper.remove();
                }
                const existingDots = dayFrame.querySelector('.event-dots-container');
                if (existingDots) {
                    existingDots.remove();
                }

                if (eventsForDay.length > 0) {
                    const eventCount = eventsForDay.length;
                    const hasBadge = eventCount > 3;
                    
                    // Create wrapper for stacked layout
                    const dotsWrapper = document.createElement('div');
                    dotsWrapper.className = 'event-dots-wrapper';
                    
                    // Different positioning based on layout type
                    if (hasBadge) {
                        // 4+ events: 3 dots + badge, positioned from top with gap for date
                        dotsWrapper.style.cssText = `
                            position: absolute;
                            top: 28px;
                            left: 0;
                            right: 0;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            gap: 1px;
                            z-index: 100;
                            pointer-events: auto;
                        `;
                    } else if (eventCount === 3) {
                        // 3 events: inverted pyramid layout, positioned at bottom
                        dotsWrapper.style.cssText = `
                            position: absolute;
                            bottom: 2px;
                            left: 2px;
                            right: 0;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            gap: 2px;
                            z-index: 100;
                            pointer-events: auto;
                        `;
                    } else {
                        // 1-2 events: centered horizontally, positioned at bottom
                        dotsWrapper.style.cssText = `
                            position: absolute;
                            top: 60%;
                            bottom: 2px;
                            left: 1px;
                            right: 0;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            gap: 1px;
                            z-index: 100;
                            pointer-events: auto;
                        `;
                    }
                    
                    // Helper function to create a dot element
                    const createDot = (event) => {
                        const color = event.backgroundColor || '#ccc';
                        const dot = document.createElement('div');
                        dot.className = 'event-dot';
                        dot.style.cssText = `
                            width: 10px;
                            height: 10px;
                            border-radius: 50%;
                            background-color: ${color};
                            flex-shrink: 0;
                            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
                            cursor: pointer;
                            transition: all 0.15s ease;
                            position: relative;
                            z-index: 101;
                            pointer-events: auto;
                        `;
                        
                        // Create hover popup
                        const eventData = event.extendedProps || {};
                        const clockIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="#9e9e9e"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>`;
                        const fireIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="#9e9e9e"><path d="M11.57 13.16c-1.36.28-2.17 1.16-2.17 2.41 0 1.34 1.11 2.42 2.49 2.42 2.05 0 3.71-1.66 3.71-3.71 0-1.07-.15-2.12-.46-3.12-.79 1.07-2.2 1.72-3.57 2zM13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/></svg>`;
                        
                        const popupHTML = `
                            <div class="event-tooltip-header" style="background-color:${color};">
                                <span class="event-tooltip-title">${event.title || 'Training'}</span>
                            </div>
                            <div class="event-tooltip-body" style="${isDarkMode ? 'background:#2d2d2d; color:#fff;' : ''}">
                                ${eventData.duration ? `
                                    <div class="event-tooltip-row">
                                        <div class="event-tooltip-icon-label">
                                            ${clockIcon}
                                            <span class="event-tooltip-label" style="${isDarkMode ? 'color:#aaa;' : ''}">Dauer</span>
                                        </div>
                                        <span class="event-tooltip-value" style="${isDarkMode ? 'color:#fff;' : ''}">${eventData.duration} min</span>
                                    </div>
                                ` : ''}
                                ${eventData.intensity ? `
                                    <div class="event-tooltip-row">
                                        <div class="event-tooltip-icon-label">
                                            ${fireIcon}
                                            <span class="event-tooltip-label" style="${isDarkMode ? 'color:#aaa;' : ''}">Intensität</span>
                                        </div>
                                        <span class="event-tooltip-value" style="${isDarkMode ? 'color:#fff;' : ''}">${eventData.intensity}/10</span>
                                    </div>
                                ` : ''}
                                ${eventData.description ? `
                                    <div class="event-tooltip-description" style="${isDarkMode ? 'color:#ccc;' : ''}">${eventData.description.substring(0, 60)}</div>
                                ` : ''}
                            </div>
                        `;
                        
                        // Add hover events
                        let popup = null;
                        
                        dot.addEventListener('mouseenter', (e) => {
                            popup = document.createElement('div');
                            popup.className = 'event-popup';
                            popup.innerHTML = popupHTML;
                            document.body.appendChild(popup);
                            
                            const rect = dot.getBoundingClientRect();
                            const popupWidth = popup.offsetWidth;
                            const popupHeight = popup.offsetHeight;
                            const viewportWidth = window.innerWidth;
                            const dotCenterX = rect.left + (rect.width / 2);
                            
                            let leftPos = dotCenterX - (popupWidth / 2) + window.scrollX;
                            
                            if (leftPos + popupWidth > viewportWidth - 20) {
                                leftPos = viewportWidth - popupWidth - 20 + window.scrollX;
                            }
                            
                            if (leftPos < 20) {
                                leftPos = 20 + window.scrollX;
                            }
                            
                            popup.style.left = `${leftPos}px`;
                            popup.style.top = `${rect.top + window.scrollY - popupHeight - 8}px`;
                        });
                        
                        dot.addEventListener('mouseleave', () => {
                            if (popup && popup.parentNode) {
                                popup.parentNode.removeChild(popup);
                                popup = null;
                            }
                        });
                        
                        dot.addEventListener('click', (e) => {
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                            e.preventDefault();
                            
                            if (popup && popup.parentNode) {
                                popup.parentNode.removeChild(popup);
                                popup = null;
                            }
                            
                            if (goToWeeklyScheduleRef.current && event.start) {
                                goToWeeklyScheduleRef.current({ 
                                    date: event.start,
                                    startDate: event.start,
                                    endDate: event.start
                                });
                            }
                        }, true);
                        
                        return dot;
                    };
                    
                    // Build layout based on event count
                    if (eventCount === 3 && !hasBadge) {
                        // Inverted pyramid: 2 dots on top, 1 on bottom
                        const topRow = document.createElement('div');
                        topRow.style.cssText = `
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            gap: 3px;
                            pointer-events: auto;
                        `;
                        topRow.appendChild(createDot(eventsForDay[0]));
                        topRow.appendChild(createDot(eventsForDay[1]));
                        
                        const bottomRow = document.createElement('div');
                        bottomRow.style.cssText = `
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            pointer-events: auto;
                        `;
                        bottomRow.appendChild(createDot(eventsForDay[2]));
                        
                        dotsWrapper.appendChild(topRow);
                        dotsWrapper.appendChild(bottomRow);
                    } else {
                        // 1-2 dots or 4+ (with badge): dots in a single row
                        const dotsContainer = document.createElement('div');
                        dotsContainer.className = 'event-dots-container';
                        dotsContainer.style.cssText = `
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            gap: 3px;
                            pointer-events: auto;
                        `;
                        
                        const maxDots = hasBadge ? 3 : eventCount;
                        const displayEvents = eventsForDay.slice(0, maxDots);
                        
                        displayEvents.forEach(event => {
                            dotsContainer.appendChild(createDot(event));
                        });
                        
                        dotsWrapper.appendChild(dotsContainer);
                    }
                    
                    // Add "+N" indicator below dots if more events
                    if (eventsForDay.length > 3) {
                        const moreBadge = document.createElement('div');
                        moreBadge.className = 'event-more-badge';
                        moreBadge.style.cssText = `
                            font-size: 9px;
                            color: #1e88e5;
                            font-weight: 700;
                            background-color: rgba(30, 136, 229, 0.15);
                            padding: 1px 6px;
                            border-radius: 6px;
                            line-height: 1.2;
                            text-align: center;
                            cursor: pointer;
                        `;
                        moreBadge.textContent = `+${eventsForDay.length - 3}`;
                        
                        // Add click handler for the badge - navigate to weekly schedule
                        // Use mousedown to prevent FullCalendar from seeing this as a date click
                        moreBadge.addEventListener('mousedown', (e) => {
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                            e.preventDefault();
                        }, true);
                        
                        moreBadge.addEventListener('click', (e) => {
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                            e.preventDefault();
                            
                            // Navigate to weekly schedule for this date
                            if (goToWeeklyScheduleRef.current && eventsForDay.length > 0 && eventsForDay[0].start) {
                                goToWeeklyScheduleRef.current({ 
                                    date: eventsForDay[0].start,
                                    startDate: eventsForDay[0].start,
                                    endDate: eventsForDay[0].start
                                });
                            }
                        }, true);
                        
                        dotsWrapper.appendChild(moreBadge);
                    }
                    
                    dayFrame.appendChild(dotsWrapper);
                }
            });
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [calendarEvents, isDarkMode, monthColumns]);

    const yPlotDataRef = useRef(yPlotData);
    useEffect(() => { yPlotDataRef.current = yPlotData }, [yPlotData]);

    const plotCurrentFocusRef = useRef(plotCurrentFocus);
    useEffect(() => { plotCurrentFocusRef.current = plotCurrentFocus }, [plotCurrentFocus]);

    const plotDataRef = useRef(plotData);
    useEffect(() => { plotDataRef.current = plotData }, [plotData]);

    const focus = {
        Kraft:          0,
        Ausdauer:       1,
        Technik:        2,
        Taktik:         3,
        Koordination:   4,
        Schnelligkeit:  5
    };

    useEffect(() => {
        if (calendarRef.current) {
            const api = calendarRef.current.getApi();
            if (api.getDate().getFullYear() !== parseInt(currentYear)) {
                api.gotoDate(`${currentYear}-01-01`);
            }
        }
    }, [currentYear]);

    useEffect(() => {  
        setCalendarDataSource([]);
        setCalendarEvents([]);
        // Only fetch when a valid athlete object with a profile is provided
        if (!(selectedAthlete && selectedAthlete.profile)) return;

        let isCancelled = false;

        (async () => {
            try {
                const response = await getWeeklyScheduleOfAthlete(selectedAthlete.profile.email, currentYear, 0);
                const weeklyScheduleResponse = response.data;

                let feedbackResponse = [];
                try {
                    const response2 = await getAllFeedbacksOfWeekOfAthlete(selectedAthlete.profile.email, currentYear, 0);
                    feedbackResponse = response2.data || [];
                } catch {
                    // No feedback yet (often a 404) should not block year calendar dots
                    feedbackResponse = [];
                }

                if (isCancelled) return;

                if (weeklyScheduleResponse !== undefined && weeklyScheduleResponse.trainingSessions !== undefined) {
                    let dataSource = [];
                    let fcEvents = [];

                    for (let i of weeklyScheduleResponse.trainingSessions) {
                        let feedback = new FeedbackData(0, 0, -1, -1, "", false);
                        let hasFeedback = false;

                        for (let f of feedbackResponse) {
                            if (f.trainingsessionid === i.trainingsessionid) {
                                feedback = new FeedbackData(f.actualDuration, f.actualIntensity, f.physicalExhaustion, f.mentalExhaustion, f.comment, f.hasNewFiles);
                                hasFeedback = true;
                            }
                        }

                        // Original Data für Plots
                        const sessionData = new TrainingSessionData(
                            new Date(i.date),
                            new Date(i.date),
                            i.trainingFocus,
                            i.description,
                            i.duration,
                            i.intensity,
                            colors2[i.trainingFocus - 1 < 6 ? 9 : i.trainingFocus - 1],
                            hasFeedback,
                            feedback
                        );

                        dataSource.push(sessionData);

                        // Mapping für FullCalendar
                        fcEvents.push({
                            title: formatTrainingFocus(i.trainingFocus),
                            start: new Date(i.date),
                            allDay: true,
                            backgroundColor: colors[i.trainingFocus - 1],
                            borderColor: 'transparent',
                            classNames: ['training-event-bar'],
                            extendedProps: {
                                ...sessionData,
                                originalData: sessionData
                            },
                        });
                    }

                    dataSource.sort((a, b) => a.focus - b.focus);
                    setCalendarDataSource(dataSource);
                    setCalendarEvents(fcEvents);
                } else {
                    setCalendarDataSource([]);
                    setCalendarEvents([]);
                }
            } catch {
                setCalendarDataSource([]);
                setCalendarEvents([]);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [selectedAthlete, currentYear, colors2, colors, refreshTrigger]);

    const zeroCummulativeTraces = useCallback((startDate, plotDat, focus, yPlotDat)=>{
        startDate.setHours(0, 0, 0, 0);
        if(yPlotDat === undefined) yPlotDat = yPlotDataRef.current;
        if (focus === undefined) focus = plotCurrentFocusRef.current;
    
        let xPlotDates = plotDat[0].x; 
        let idxStartDate = xPlotDates.findIndex(dt => dt.getTime() === startDate.getTime());
        // Use the index before startDate for offset calculation, so startDate's data is included
        let offsetIdx = idxStartDate > 0 ? idxStartDate - 1 : -1;
    
        // cum duration
        let yPlotDuration = yPlotDat[focus].duration;
        let cumDuration = yPlotDuration.map((sum => value => sum += value)(0));
        let offset = offsetIdx >= 0 ? cumDuration[offsetIdx] : 0;
        plotDat[1].y = cumDuration.map(val => val -= offset).map(val => Math.max(val, 0));
    
        // cum intensity
        let yPlotIntensity = yPlotDat[focus].intensity;
        let cumIntensity = yPlotIntensity.map((sum => value => sum += value)(0));
        offset = offsetIdx >= 0 ? cumIntensity[offsetIdx] : 0;
        plotDat[3].y = cumIntensity.map(val => val -= offset).map(val => Math.max(val, 0));
    
        // cum load
        let yPlotLoad = yPlotDat[focus].load;
        let cumLoad = yPlotLoad.map((sum => value => sum += value)(0));
        offset = offsetIdx >= 0 ? cumLoad[offsetIdx] : 0;
        plotDat[7].y = cumLoad.map(val => val -= offset).map(val => Math.max(val, 0));
    
        // cum actual load
        let yPlotActualLoad = yPlotDat[focus].actualLoad;
        let cumActualLoad = yPlotActualLoad.map((sum => value => sum += value)(0));
        offset = offsetIdx >= 0 ? cumActualLoad[offsetIdx] : 0;
        plotDat[9].y = cumActualLoad.map(val => val -= offset).map(val => Math.max(val, 0));
    },[]);

    useEffect(() =>{ 
        if(showPlots && calendarDataSource.length > 0){
            // ... (Plot Logic unchanged)
            let getDaysArray = function(start, end) {
            for(var arr=[], dt=new Date(start); dt<=new Date(end); dt.setDate(dt.getDate()+1)){ arr.push(new Date(dt)); }
            return arr;
        };  
        let startDate = new Date(currentYear + "-01-01T00:00:00.000");    
        let endDate = new Date(currentYear + "-12-31T00:00:00.000");
        let xPlotDates = getDaysArray(startDate, endDate);
        let getFocusFromCalendarData = function( focus ){
            let yPlotFocus = [];
            for(let i of xPlotDates){
                yPlotFocus.push(calendarDataSource.filter( data => data.startDate.getTime() === i.getTime() && data.focus === focus));
            }     
            return yPlotFocus;
        } 
        
        let yPlotFocus = [];
        for (let focus = 1; focus < 7; focus++){
            let yPlotDuration = [];
            let yPlotIntensity = [];
            let yPlotActualDuration = [];
            let yPlotActualIntensity = [];
            let yPlotPhysicalExhaustion = [];
            let yPlotMentalExhaustion = [];
            let data = getFocusFromCalendarData(focus);
            for (let i of data ){
                yPlotDuration.push(  i.length === 0 ? 0 : i.reduce((partialSum, a) => partialSum + a.duration, 0));  
                yPlotIntensity.push(  i.length === 0 ? 0 : i.reduce((partialSum, a) => partialSum + a.intensity, 0));
                yPlotActualDuration.push(  i.length === 0 ? 0 : i.reduce((partialSum, a) => partialSum + a.feedback.actualDuration, 0));
                yPlotActualIntensity.push( i.length === 0 ? 0 : i.reduce((partialSum, a) => partialSum + a.feedback.actualIntensity, 0));
                yPlotPhysicalExhaustion.push(i.length === 0 ? -1 : Math.round(i.reduce((partialSum, a) => partialSum + a.feedback.physicalExhaustion, 0) / i.length));  
                yPlotMentalExhaustion.push(i.length === 0 ? -1 : Math.round(i.reduce((partialSum, a) => partialSum + a.feedback.mentalExhaustion, 0) / i.length));
            }
            let yPlotTrainingLoad = yPlotDuration.map((v,i) => v * yPlotIntensity[i]);
            let yPlotActualTrainingLoad = yPlotActualDuration.map((v,i) => v * yPlotActualIntensity[i]);
        
            for(let i in yPlotPhysicalExhaustion){
                yPlotPhysicalExhaustion[i] =  yPlotDuration[i] > 0 ? yPlotPhysicalExhaustion[i] : -2 ; 
                yPlotMentalExhaustion[i]   =  yPlotDuration[i] > 0 ? yPlotMentalExhaustion[i]   : -2 ;
            }

            yPlotFocus.push({   duration:           yPlotDuration, 
                                intensity:          yPlotIntensity,
                                actualDuration:     yPlotActualDuration,
                                actualIntensity:    yPlotActualIntensity,
                                physicalExhaustion: yPlotPhysicalExhaustion,
                                mentalExhaustion:   yPlotMentalExhaustion,
                                load:               yPlotTrainingLoad,
                                actualLoad:         yPlotActualTrainingLoad,
                                cumDuration:        yPlotDuration.map((sum => value => sum += value)(0)),
                                cumIntensity:       yPlotIntensity.map((sum => value => sum += value)(0)),
                                cumLoad:            yPlotTrainingLoad.map((sum => value => sum += value)(0)),
                                cumActualLoad:      yPlotActualTrainingLoad.map((sum => value => sum += value)(0))
                            })
        }

        // Fixed color palette for chart traces (consistent regardless of focus)
        const chartColors = {
            dauer: '#42a5f5',              // Blue
            intensitaet: '#ef5350',        // Red
            rueckmeldungDauer: '#a1887f',  // Light brown (for Rückmeldung Dauer)
            rueckmeldungIntensitaet: '#6d4c41', // Dark brown (for Rückmeldung Intensität)
            rueckmeldungLoad: '#d7ccc8',   // Very light brown (for Rückmeldung Load)
            load: '#9c27b0',               // Purple
            erschoepfung: '#388e3c',       // Darker green
        };

        let initialPlotData = [{
            name: 'Dauer',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].duration,       
            type: 'bar',
            hovertemplate: '<b>Dauer</b>: %{y} min<extra></extra>',
            marker: { color: chartColors.dauer, opacity: 0.7 },
            visible: true
        },
        {
            name: 'Dauer (kum.)',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].cumDuration,
            type: 'scatter',
            mode: 'lines',
            hovertemplate: '<b>Dauer (kumulativ)</b>: %{y} min<extra></extra>',
            line: { color: chartColors.dauer, width: 3, shape: 'spline' },
            visible: true,
        },
        {
            name: 'Intensität',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].intensity, 
            type: 'bar',
            hovertemplate: '<b>Intensität</b>: %{y}/10<extra></extra>',
            marker: { color: chartColors.intensitaet, opacity: 0.7 },
            visible: true,
        },
        {
            name: 'Intensität (kum.)',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].cumIntensity, 
            type: 'scatter',
            mode: 'lines',
            hovertemplate: '<b>Intensität (kumulativ)</b>: %{y}<extra></extra>',
            line: { color: chartColors.intensitaet, width: 3, shape: 'spline' },
            visible: true,
        },
        {
            name: 'Rückmeldung Dauer',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].actualDuration, 
            type: 'bar',
            hovertemplate: '<b>Rückmeldung Dauer</b>: %{y} min<extra></extra>',
            marker: { color: chartColors.rueckmeldungDauer },
            visible: true
        },
        {
            name: 'Rückmeldung Intensität',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].actualIntensity,
            type: 'bar',
            hovertemplate: '<b>Rückmeldung Intensität</b>: %{y}/10<extra></extra>',
            marker: { color: chartColors.rueckmeldungIntensitaet },
            visible: true,
        },
        {
            name: 'Load',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].load,
            type: 'bar',
            hovertemplate: '<b>Training Load</b>: %{y}<extra></extra>',
            marker: { color: chartColors.load, opacity: 0.4 },
            visible: false,
        },
        {
            name: 'Load (kum.)',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].cumLoad, 
            type: 'scatter',
            mode: 'lines',
            hovertemplate: '<b>Load (kumulativ)</b>: %{y}<extra></extra>',
            line: { color: chartColors.load, width: 2, dash: 'dot' },
            visible: false,
        },
        {
            name: 'Rückmeldung Load',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].actualLoad, 
            type: 'bar',
            hovertemplate: '<b>Rückmeldung Load</b>: %{y}<extra></extra>',
            marker: { color: chartColors.rueckmeldungLoad },
            visible: false,
        },  
        {
            name: 'Training load (kumulativ)',
            x : xPlotDates,
            y : yPlotFocus[focus.Kraft].cumActualLoad,
            type: 'scatter',
            mode: 'lines',
            hovertemplate: '<b>Training Load (kumulativ)</b>: %{y}<extra></extra>',
            line: { color: chartColors.rueckmeldungLoad, width: 2, dash: 'dot' },
            visible: false,
        },
        {
            name: 'Physische Erschöpfung',
            x : xPlotDates,
            y : Array(xPlotDates.length).fill(15), 
            type: 'scatter',
            mode: 'markers',
            hovertemplate: '<b>Physische Erschöpfung</b><extra></extra>',
            marker: { 
                color: yPlotFocus[focus.Kraft].physicalExhaustion.map((val) => getColorFromExhaustion(val, false)), 
                size: 10,
                line: { width: 1, color: isDarkMode ? '#444' : '#fff' }
            },
            visible: false,
        },
        {
            name: 'Mentale Erschöpfung',
            x : xPlotDates,
            y : Array(xPlotDates.length).fill(0),
            type: 'scatter',
            mode: 'markers',
            hovertemplate: '<b>Mentale Erschöpfung</b><extra></extra>',
            marker: { 
                color: yPlotFocus[focus.Kraft].mentalExhaustion.map((val) => getColorFromExhaustion(val, false)), 
                size: 10,
                line: { width: 1, color: isDarkMode ? '#444' : '#fff' }
            },
            visible: false,
        }];

        let initialLayout = {  
            autosize: true,
            height: isTouchDevice ? 350 : 500, 
            margin: isTouchDevice ? { t: 30, b: 40, l: 35, r: 10 } : { t: 40, b: 50, l: 50, r: 20 },
            paper_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            plot_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            font: {
                family: 'Inter, sans-serif',
                color: isDarkMode ? '#e0e0e0' : '#333333'
            },
            showlegend: false,
            hovermode: 'x unified',
            datarevision: Date.now(),
            xaxis: {
                range: [selectedDateRange.start, selectedDateRange.end],
                type: 'date',
                title: {
                    text: '[SHIFT] + Leertaste: geblätterten Zeitraum anpassen',
                    font: {
                      size: 11,
                      color: isDarkMode ? '#666' : '#999'
                    }
                },
                tickfont: { color: isDarkMode ? '#bbb' : '#555', size: 11 },
                gridcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                zerolinecolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
            },
            yaxis: {
                autorange: true,
                autoscale: true,
                type: 'linear',
                rangemode: 'nonnegative',
                tickfont: { color: isDarkMode ? '#bbb' : '#555', size: 11 },
                gridcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                zerolinecolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
            },
            uirevision: 'time',
        };
        setPlotRevision(prev => prev +1);

        if(plotData.length === 0) {
            zeroCummulativeTraces(selectedDateRange.start, initialPlotData, focus.Kraft, yPlotFocus)
            setPlotData(initialPlotData );
            setPlotLayout(initialLayout );
        }
        // Reset legend visibility to match initial trace visibility when dialog opens
        setLegendVisibility({
            dauer: true,
            dauerKum: true,
            intensitaet: true,
            intensitaetKum: true,
            rueckmeldungDauer: true,
            rueckmeldungIntensitaet: true,
            load: false,
            loadKum: false,
            rueckmeldungLoad: false,
            trainingLoadKum: false,
            physischeErschoepfung: false,
            mentaleErschoepfung: false,
        });
        setYPlotData(yPlotFocus);

        // Pie chart
        // data
        let pieData = [{
            type: 'pie',
            hole: .4,   
            values: [],
            labels: [formatTrainingFocus(focus.Kraft+1), formatTrainingFocus(focus.Ausdauer+1), formatTrainingFocus(focus.Technik+1), formatTrainingFocus(focus.Taktik+1), formatTrainingFocus(focus.Koordination+1), formatTrainingFocus(focus.Schnelligkeit+1)],
            marker: {colors: colors.slice(0,6)},
            text: [],
            textinfo: 'label+percent',
            textposition: 'inside',
            insidetextorientation: 'horizontal',
            automargin: true,
            hovertext: [],
            hoverinfo: 'label+text',
            // Hide labels for 0% slices by using texttemplate
            texttemplate: '%{percent:.0%}',
            insidetextfont: {
                color: '#000000',
                size: 13,
                family: 'Inter, sans-serif',
                weight: 700
            },
            // Only show text if value > 0
            sort: false,
        }];
        setPlotDataPieChart(pieData);

        // Pie chart
        // layout
        let pieLayout = {
            autosize: true,
            height: isTouchDevice ? 300 : 450,
            margin: isTouchDevice ? { t: 30, b: 60, l: 20, r: 20 } : { t: 40, b: 80, l: 40, r: 40 },
            title: [],
            paper_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            plot_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            font: {
                color: isDarkMode ? '#ffffff' : '#333333'
            },
            // Show legend horizontally at bottom
            showlegend: true,
            legend: {
                orientation: 'h',
                x: 0.5,
                xanchor: 'center',
                y: -0.15,
                yanchor: 'top',
                font: {
                    size: 11,
                    color: isDarkMode ? '#ffffff' : '#333333'
                }
            },
            annotations: [{
                x: 0.5,
                y: 0.5,
                align: 'center',
                text: "Soll",
                font: {
                    family: 'Inter, sans-serif',
                    color: isDarkMode ? '#ffffff' : '#333333',
                    size: 16,
                    weight: 600
                },
                xref: "paper",
                yref: "paper",
                showarrow: false
            }]
        };
        setPlotLayoutPieChart(pieLayout);

        // Bar chart (feedback summary)
        // data
        let barData = {
            name: 'Dauer',
            type: 'bar',
            x: ['Kraft', 'Ausdauer', 'Technik', 'Taktik', 'Koordination', 'Schnelligkeit'],
            y: [],
            marker: { color: colors.slice(0,6) },
            hovertemplate: '<b>%{x}</b><br>Dauer: %{y:.1f}%<extra></extra>',
        };
        setPlotDataBarChart(barData);

        let barData2 = {
            name: 'Intensität',
            type: 'bar',
            x: ['Kraft', 'Ausdauer', 'Technik', 'Taktik', 'Koordination', 'Schnelligkeit'],
            y: [],
            marker: { color: colors.slice(0,6), opacity: 0.6 },
            hovertemplate: '<b>%{x}</b><br>Intensität: %{y:.1f}%<extra></extra>',
        };
        setPlotDataBarChart2(barData2);

        // Bar chart
        // layout
        let barLayout = {
            autosize: true,
            height: isTouchDevice ? 300 : 450,
            margin: isTouchDevice ? { t: 80, b: 50, l: 45, r: 20 } : { t: 100, b: 60, l: 60, r: 40 },
            paper_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            plot_bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            font: {
                color: isDarkMode ? '#ffffff' : '#333333'
            },
            yaxis: { 
                title: 'Anteil [%]', 
                titlefont: { size: 12, color: isDarkMode ? '#b0b0b0' : '#666666' },
                tickfont: { color: isDarkMode ? '#b0b0b0' : '#666666', size: 11 },
                gridcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                rangemode: 'nonnegative'
            },
            xaxis: { 
                title: {
                    text: 'Ist',
                    font: {
                        size: 16,
                        color: isDarkMode ? '#ffffff' : '#333333',
                        family: 'Inter, sans-serif',
                        weight: 600
                    }
                },
                tickfont: { color: isDarkMode ? '#b0b0b0' : '#666666', size: 11 }
            },
            title: {
                text: 'Tatsächliche Dauer & Intensität',
                font: { size: 16, color: isDarkMode ? '#ffffff' : '#333333', family: 'Inter, sans-serif', weight: 600 },
                y: 0.95
            },
            bargroupgap: 0.2,
            bargap: 0.3,
            barmode: 'group',
            showlegend: true,
            legend: {
                orientation: 'h',
                x: 0.5,
                xanchor: 'center',
                y: 1.08,
                yanchor: 'top',
                font: {
                    size: 12,
                    color: isDarkMode ? '#ffffff' : '#333333'
                }
            },
        };
        setPlotLayoutBarChart(barLayout);
        }
    }, [showPlots,calendarDataSource,currentYear,focus.Ausdauer,focus.Kraft,focus.Technik,focus.Taktik,focus.Koordination,focus.Schnelligkeit,colors2,colors3,colors,plotData.length,selectedDateRange.start,selectedDateRange.end,zeroCummulativeTraces,isDarkMode,isTouchDevice]); 

    useEffect(() =>{
        window.addEventListener('keyup', ( e ) => { keyboardEvent(e) });
        return () => {
            window.removeEventListener('keyup', ( e ) => { keyboardEvent(e) });
        };
    },[]);

    const setUnderlineStyleOfPlotButton = useCallback((startDate, endDate)=>{
        // Guard against undefined refs during initial render or hot reload
        if (!plotDataRef.current || !plotDataRef.current[0] || !yPlotDataRef.current) {
            return;
        }
        let xPlotDates = plotDataRef.current[0].x;
        if (!xPlotDates) return;
        let idxStartDate = xPlotDates.findIndex(dt => dt.getTime() === startDate.getTime());
        let idxEndDate = xPlotDates.findIndex(dt => dt.getTime() === endDate.getTime());
        if (idxStartDate === -1 || idxEndDate === -1) return;
        let style = [];
        for(let i = focus.Kraft; i<= focus.Schnelligkeit; i++){
            if (!yPlotDataRef.current[i]) continue;
            let exhaustion = yPlotDataRef.current[i].mentalExhaustion.slice(idxStartDate, idxEndDate + 1).concat(yPlotDataRef.current[i].physicalExhaustion.slice(idxStartDate, idxEndDate + 1));
            let ma = Math.max(...exhaustion);
            style.push(getColorFromExhaustion( ma, false ));
        }
        setPlotExhaustionColors(style);
    },[focus.Kraft, focus.Schnelligkeit]);
    
    useEffect(() =>{
        if(plotLayout.xaxis !== undefined){
            let dateRange = plotLayout.xaxis.range;     
            let startDate = new Date(dateRange[0]);
            let endDate   = new Date(dateRange[1]);
            startDate.setHours(0,0,0,0);
            endDate.setHours(0,0,0,0);
            setUnderlineStyleOfPlotButton(startDate, endDate);
        }
    },[plotLayout.xaxis, setUnderlineStyleOfPlotButton]);

    useEffect(() => { 
        if(showPlots && pressedKey !== "none"){ 
            const layout = Object.assign({}, plotLayout);
            const oldRange = [new Date(layout.xaxis.range[0]), new Date(layout.xaxis.range[1])];
            const intervalDays = Math.round((oldRange[1] - oldRange[0]) / 1000/60/60/24) ;
            let newRange0 = undefined;
            let newRange1 = undefined;

            if(pressedKey === "Space" ) {
                newRange0 = new Date(oldRange[1]);
                newRange0.setDate(newRange0.getDate() + 1);
                newRange1 = new Date(newRange0);
                newRange1.setDate(newRange1.getDate() + intervalDays);
            } else if
                (pressedKey === "Space+Shift") {
                    newRange1 = new Date(oldRange[0]);
                    newRange1.setDate(newRange1.getDate() - 1 );
                    newRange0 = new Date(newRange1);
                    newRange0.setDate(newRange0.getDate() - intervalDays);
                } else {
                    return;
                }
            let newRangeStart = new Date(newRange0);
            let newRangeEnd = new Date(newRange1);        
            newRangeStart.setHours(0, 0, 0, 0);
            newRangeEnd.setHours(0, 0, 0, 0);
            layout.xaxis.range = [newRangeStart, newRangeEnd];
            layout.datarevision++;
            setPlotLayout(layout);
            setPlotRevision( prev => prev +1 );
            setPressedKey("none");
            setUnderlineStyleOfPlotButton(newRangeStart, newRangeEnd);
        }
    },[plotLayout, pressedKey, setUnderlineStyleOfPlotButton, showPlots]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const todayEl = document.querySelector('.fc-day-today') || document.querySelector('.fc-day-today-custom');
            if (todayEl) {
                // Suche das übergeordnete Monats-Container-Element
                const monthEl = todayEl.closest('.fc-multimonth-month');
                
                // Wenn der Monat gefunden wird, scrolle diesen in die Mitte.
                // Falls nicht (z.B. andere Ansicht), scrolle zum Tag.
                const targetEl = monthEl || todayEl;

                // block: 'center' sorgt dafür, dass der ganze Monat tendenziell mittig ist
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

                setTimeout(() => {
                    todayEl.classList.add('flash-active');
                }, 200); 
                setTimeout(() => {
                    todayEl.classList.remove('flash-active');
                }, 800); 
            }
        }, 300); 

        return () => clearTimeout(timer);
    }, [currentYear, showPlots, windowWidth]);

    function keyboardEvent(e){
        if(!e.shiftKey && e.keyCode === 32 ) {
            setPressedKey("Space");
        } else if
            (e.shiftKey && e.keyCode === 32) {
                setPressedKey("Space+Shift");
            } else {
                setPressedKey("none");
                return;
            }
    }

    const handleClose = () => {
        setShowPlots(false);
        setPlotData([]);
        setPlotPieChart(false);
        setPlotDataPieChart([]);
    };

    const onClickPieChartButton = () =>{
        let dateRange = plotLayout.xaxis.range;     
        let startDate = new Date(dateRange[0]);
        let endDate   = new Date(dateRange[1]);
        startDate.setHours(0,0,0,0);
        endDate.setHours(0,0,0,0);
        setPlotPieChart(!plotPieChart);
        let xPlotDates = plotData[0].x;
        let idxStartDate = xPlotDates.findIndex(dt => dt.getTime() === startDate.getTime());
        let idxEndDate = xPlotDates.findIndex(dt => dt.getTime() === endDate.getTime());
        // Use the index before startDate for offset calculation, so startDate's data is included
        let offsetIdx = idxStartDate > 0 ? idxStartDate - 1 : -1;
        let numDaysInDateRange = (endDate - startDate)/(24*60*60*1000)+1;
        let totalDurations = [];
        let totalIntensity = [];
        let totalActualDurations = [];
        let totalActualIntensity = [];
        let numIntensity = [];
        for(let i = focus.Kraft; i<= focus.Schnelligkeit; i++){
            // stats over training sessions
            // durations
            let yPlotDuration = yPlotData[i].duration;
            let cumDuration = yPlotDuration.map((sum => value => sum += value)(0));
            let offset = offsetIdx >= 0 ? cumDuration[offsetIdx] : 0;
            totalDurations.push(cumDuration[idxEndDate]-offset);

            // intensities
            let yPlotIntensity = yPlotData[i].intensity;
            numIntensity.push(yPlotIntensity.slice(idxStartDate, idxEndDate+1).filter(val => val > 0).length);
            let cumIntensity = yPlotIntensity.map((sum => value => sum += value)(0));
            offset = offsetIdx >= 0 ? cumIntensity[offsetIdx] : 0;
            totalIntensity.push( cumIntensity[idxEndDate]-offset );

            let yPlotActualDuration = yPlotData[i].actualDuration;
            let cumActualDuration = yPlotActualDuration.map((sum => value => sum += value)(0));
            offset = offsetIdx >= 0 ? cumActualDuration[offsetIdx] : 0;
            totalActualDurations.push(cumActualDuration[idxEndDate]-offset);
        
            //  intensities 
            let yPlotActualIntensity = yPlotData[i].actualIntensity;
            let cumActualIntensity = yPlotActualIntensity.map((sum => value => sum += value)(0));
            offset = offsetIdx >= 0 ? cumActualIntensity[offsetIdx] : 0;
            totalActualIntensity.push(cumActualIntensity[idxEndDate]-offset);  
        }

        let sumTotalDurations= totalDurations.reduce((a, b) => a + b, 0);
        let ratioDurations = totalDurations.map( dur => dur/sumTotalDurations);
        let ratioAcutalDuration = totalActualDurations.map((val,i) => (val / totalDurations[i]).toFixed(2) * 100.0);
        let ratioActualIntensity = totalActualIntensity.map((val,i) => (val / totalIntensity[i]).toFixed(2) * 100.0);
        let averageActualIntensity = totalActualIntensity.map((val, i) => (val / numIntensity[i]).toFixed(1)); 

        // pie data
        let pieData = plotDataPieChart;
        pieData[0].values = ratioDurations;
        pieData[0].text  = [`${totalDurations[focus.Kraft]} min.`, `${totalDurations[focus.Ausdauer]} min.`, `${totalDurations[focus.Technik]} min.`, `${totalDurations[focus.Taktik]} min.`, `${totalDurations[focus.Koordination]} min.`, `${totalDurations[focus.Schnelligkeit]} min.`];
        pieData[0].hovertext = [`${totalDurations[focus.Kraft]} min. gesamt`, `${totalDurations[focus.Ausdauer]} min. gesamt`, `${totalDurations[focus.Technik]} min. gesamt`, `${totalDurations[focus.Taktik]} min. gesamt`, `${totalDurations[focus.Koordination]} min. gesamt`, `${totalDurations[focus.Schnelligkeit]} min. gesamt`];
        setPlotDataPieChart(pieData);

        // pie layout
        let pieLayout = plotLayoutPieChart;
        pieLayout.title = { text: 'Anteile an der Gesamttrainingsdauer', font: { size: 18, family: 'Inter, sans-serif', weight: 600 } };
        setPlotLayoutPieChart(pieLayout);
        setPlotPieBarChartTitle(`Soll-Ist Vergleich im Zeitraum ${startDate.toLocaleDateString("de-DE", { month: '2-digit', day: '2-digit' })} bis ${endDate.toLocaleDateString("de-DE", { year: 'numeric', month: '2-digit', day: '2-digit' })} (${numDaysInDateRange.toFixed(0)} Tage)`);

        // bar data
        let barData = plotDataBarChart;
        barData.y = ratioAcutalDuration;
        barData.text = totalActualDurations.map((val) => `${val} min.`);
        setPlotDataBarChart(barData);

        barData = plotDataBarChart2;
        barData.y = ratioActualIntensity;
        barData.text = averageActualIntensity.map((val) => `⌀ ${val} / 10`);
        setPlotDataBarChart2(barData);

        // bar layout
        let barLayout = plotLayoutBarChart;
        barLayout.title = { text: 'Tatsächliche Dauer & Intensität', font: { size: 16, family: 'Inter, sans-serif', weight: 600 } };
    }   

    const onClickPlotButton = (focus2) => {
        let plotDat = plotData;
        plotDat[0].y = yPlotData[focus2].duration;
        plotDat[2].y = yPlotData[focus2].intensity;
        plotDat[4].y = yPlotData[focus2].actualDuration;
        plotDat[5].y = yPlotData[focus2].actualIntensity;
        plotDat[6].y = yPlotData[focus2].load;
        plotDat[8].y = yPlotData[focus2].actualLoad;

        let dateRange = plotLayout.xaxis.range;     
        let startDate = new Date(dateRange[0]);

        zeroCummulativeTraces(startDate, plotDat, focus2);
        
        // marker - use fixed chart colors
        plotDat[0].marker = { color: '#42a5f5', opacity: 0.7 }; // Dauer - Blue
        plotDat[1].line = { color: '#42a5f5', width: 3, shape: 'spline' }; // Dauer kum
        plotDat[2].marker = { color: '#ef5350', opacity: 0.7 }; // Intensität - Red
        plotDat[3].line = { color: '#ef5350', width: 3, shape: 'spline' }; // Intensität kum
        plotDat[4].marker = { color: '#a1887f' }; // Rückmeldung Dauer - Light brown
        plotDat[5].marker = { color: '#6d4c41' }; // Rückmeldung Intensität - Dark brown
        plotDat[6].marker = { color: '#9c27b0', opacity: 0.4 }; // Load - Purple
        plotDat[7].line = { color: '#9c27b0', width: 2, dash: 'dot' }; // Load kum - Purple
        plotDat[8].marker = { color: '#d7ccc8' }; // Rückmeldung Load - Very light brown
        plotDat[9].line = { color: '#d7ccc8', width: 2, dash: 'dot' }; // Training load kum - Very light brown

        // layout
        const layout = Object.assign({}, plotLayout);
        layout.datarevision++;
        // Remove title update since we removed internal title
        
        setPlotLayout(layout);
        
        setPlotLayout(layout);
        setPlotRevision(plotRevision +1);
        setPlotCurrentFocus(focus2);
        setPlotPieChart(false);
    };

    function onXaxisZoomPan(figure){
        let startDate = new Date(figure.layout.xaxis.range[0]);
        let endDate = new Date(figure.layout.xaxis.range[1]);
        startDate.setHours(0,0,0,0);
        endDate.setHours(0,0,0,0);
        
        // clamp dates to first and last day of the year. Necessary, if autoscale button in the ploty button-panel is clicked    
        if (startDate < (new Date(currentYear + "-01-01T00:00:00.000"))){
            startDate = new Date(currentYear + "-01-01T00:00:00.000");
        }
        if (endDate > (new Date(currentYear + "-12-31T00:00:00.000"))){
            endDate = new Date(currentYear + "-12-31T00:00:00.000");
        }
        let plotDat = plotData; 
        zeroCummulativeTraces(startDate, plotDat);
        setPlotData(plotDat);
        setUnderlineStyleOfPlotButton(startDate, endDate);
    }

    // Legend menu handlers
    const handleLegendMenuOpen = (group, event) => {
        setLegendMenuAnchor(prev => ({ ...prev, [group]: event.currentTarget }));
    };
    
    const handleLegendMenuClose = (group) => {
        setLegendMenuAnchor(prev => ({ ...prev, [group]: null }));
    };
    
    // Trace index mapping for visibility toggle
    const traceIndexMap = {
        dauer: 0,
        dauerKum: 1,
        intensitaet: 2,
        intensitaetKum: 3,
        rueckmeldungDauer: 4,
        rueckmeldungIntensitaet: 5,
        load: 6,
        loadKum: 7,
        rueckmeldungLoad: 8,
        trainingLoadKum: 9,
        physischeErschoepfung: 10,
        mentaleErschoepfung: 11,
    };
    
    const toggleLegendItem = (key) => {
        const newVisibility = !legendVisibility[key];
        setLegendVisibility(prev => ({ ...prev, [key]: newVisibility }));
        
        // Update plotData visibility
        const plotDat = [...plotData];
        const idx = traceIndexMap[key];
        if (plotDat[idx]) {
            plotDat[idx].visible = newVisibility;
            setPlotData(plotDat);
            setPlotRevision(prev => prev + 1);
        }
    };

    const handleDateClick = (arg) => {
        // Don't allow clicking on grayed-out dates (dates from adjacent months)
        if (arg.dayEl && arg.dayEl.classList.contains('fc-day-other')) {
            return;
        }
        
        // Check if the click was on an event dot - if so, don't navigate to create session
        // (the dot's own click handler will handle navigation to weekly schedule)
        if (arg.jsEvent && arg.jsEvent.target) {
            const target = arg.jsEvent.target;
            if (target.classList.contains('event-dot') || target.closest('.event-dot')) {
                return;
            }
        }
        
        prepCreateWeeklySchedule({ 
            date: arg.date,
            startDate: arg.date,
            endDate: arg.date
        });
    };

    const handleRangeSelect = (arg) => {
        const diff = arg.end.getTime() - arg.start.getTime();
        const oneDay = 1000 * 60 * 60 * 24;
        if (diff <= oneDay) {
            arg.view.calendar.unselect();
            return; 
        }
        // FullCalendar's end date is exclusive, so subtract one day to get the inclusive end date
        const inclusiveEnd = new Date(arg.end);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        setSelectedDateRange({ start: arg.start, end: inclusiveEnd });
        setShowPlots(true);
    };

    // Prevent selection from starting on grayed-out dates
    const selectAllow = (selectInfo) => {
        // Check if any day cell for this date has fc-day-other class
        const dateStr = selectInfo.startStr.split('T')[0];
        const dayCells = document.querySelectorAll(`.fc-daygrid-day[data-date="${dateStr}"]`);
        
        for (const cell of dayCells) {
            if (cell.classList.contains('fc-day-other')) {
                return false;
            }
        }
        return true;
    };

    const renderDayCellClassNames = (arg) => {
        let classes = [];
        const today = new Date();
        today.setHours(0,0,0,0);
        const cellDate = arg.date;
        cellDate.setHours(0,0,0,0);
        if (cellDate.getTime() === today.getTime()) {
            classes.push('fc-day-today-custom'); 
        }
        if (getWeek(cellDate) === selectedCalenderWeek) {
            classes.push('fc-day-selected-week');
        }
        return classes;
    };

    const handleEventClick = (arg) => {
        // Navigate to weekly schedule view when clicking on an event dot
        if (goToWeeklySchedule) {
            goToWeeklySchedule({ 
                date: arg.event.start,
                startDate: arg.event.start,
                endDate: arg.event.start
            });
        }
    };

    const handleEventDidMount = (info) => {
        const eventData = info.event.extendedProps;
        const color = info.event.backgroundColor;
        
        const popupHTML = `
            <div class="event-tooltip-header" style="background-color:${color};">
                <span class="event-tooltip-title">${info.event.title}</span>
            </div>
            <div class="event-tooltip-body">
                <div class="event-tooltip-row">
                    <span class="event-tooltip-label">Dauer</span>
                    <span class="event-tooltip-value">${eventData.duration} min</span>
                </div>
                <div class="event-tooltip-row">
                    <span class="event-tooltip-label">Intensität</span>
                    <span class="event-tooltip-value">${eventData.intensity}/10</span>
                </div>
                ${eventData.description ? `
                    <div class="event-tooltip-description">${eventData.description.substring(0, 50)}</div>
                ` : ''}
            </div>
        `;
        
        let popup = null;
        
        info.el.addEventListener('mouseenter', () => {
            popup = document.createElement('div');
            popup.className = 'event-popup';
            popup.innerHTML = popupHTML;
            document.body.appendChild(popup);
            
            const rect = info.el.getBoundingClientRect();
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.top + window.scrollY - popup.offsetHeight - 8}px`;
        });
        
        info.el.addEventListener('mouseleave', () => {
            if (popup && popup.parentNode) {
                popup.parentNode.removeChild(popup);
                popup = null;
            }
        });
        
        // Remove popup on click before navigation
        info.el.addEventListener('click', () => {
            if (popup && popup.parentNode) {
                popup.parentNode.removeChild(popup);
                popup = null;
            }
        });
    };

    const handleDatesSet = (arg) => {
        const year = arg.view.currentStart.getFullYear();
        if (year !== parseInt(currentYear) && onYearChange) {
            onYearChange(year);
        }
    };

    // =============================================
    // Mobile Touch Selection Handlers
    // =============================================
    
    const handleTouchStart = useCallback((e) => {
        if (!isTouchDevice) return;
        
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const dayCell = element?.closest('.fc-daygrid-day:not(.fc-day-other)');
        
        if (!dayCell) return;
        
        const dateStr = dayCell.getAttribute('data-date');
        if (!dateStr) return;
        
        // Start long-press timer
        touchTimerRef.current = setTimeout(() => {
            setTouchStartCell(dateStr);
            setTouchCurrentCell(dateStr);
            setIsTouchSelecting(true);
            // Add class to indicate selection is active (for CSS)
            document.querySelector('.fc')?.classList.add('touch-selecting-active');
            // Haptic feedback if available
            if (navigator.vibrate) navigator.vibrate(50);
        }, LONG_PRESS_DELAY);
    }, [isTouchDevice, LONG_PRESS_DELAY]);

    const handleTouchMove = useCallback((e) => {
        // Cancel long-press timer if finger moves before activation
        if (!isTouchSelecting && touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
            touchTimerRef.current = null;
            return;
        }
        
        if (!isTouchSelecting) return;
        
        e.preventDefault(); // Prevent scrolling during selection
        
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const dayCell = element?.closest('.fc-daygrid-day:not(.fc-day-other)');
        
        if (dayCell) {
            const dateStr = dayCell.getAttribute('data-date');
            if (dateStr && dateStr !== touchCurrentCell) {
                setTouchCurrentCell(dateStr);
                // Haptic feedback on cell change
                if (navigator.vibrate) navigator.vibrate(20);
            }
        }
    }, [isTouchSelecting, touchCurrentCell]);

    const handleTouchEnd = useCallback(() => {
        // Clear long-press timer
        if (touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
            touchTimerRef.current = null;
        }
        
        // Remove selection active class
        document.querySelector('.fc')?.classList.remove('touch-selecting-active');
        
        if (isTouchSelecting && touchStartCell && touchCurrentCell) {
            // Calculate the date range
            const startDate = new Date(touchStartCell);
            const endDate = new Date(touchCurrentCell);
            
            // Ensure start <= end
            const actualStart = startDate <= endDate ? startDate : endDate;
            const actualEnd = startDate <= endDate ? endDate : startDate;
            
            // Only open dialog if range spans at least one day
            const diff = actualEnd.getTime() - actualStart.getTime();
            const oneDay = 24 * 60 * 60 * 1000;
            
            if (diff >= oneDay) {
                setSelectedDateRange({ start: actualStart, end: actualEnd });
                setShowPlots(true);
            }
        }
        
        // Reset touch selection state
        setTouchStartCell(null);
        setTouchCurrentCell(null);
        setIsTouchSelecting(false);
    }, [isTouchSelecting, touchStartCell, touchCurrentCell]);

    // Register touch event listeners on the calendar
    useEffect(() => {
        if (!isTouchDevice) return;
        
        const calendarEl = document.querySelector('.fc');
        if (!calendarEl) return;
        
        calendarEl.addEventListener('touchstart', handleTouchStart, { passive: true });
        calendarEl.addEventListener('touchmove', handleTouchMove, { passive: false });
        calendarEl.addEventListener('touchend', handleTouchEnd);
        calendarEl.addEventListener('touchcancel', handleTouchEnd);
        
        return () => {
            calendarEl.removeEventListener('touchstart', handleTouchStart);
            calendarEl.removeEventListener('touchmove', handleTouchMove);
            calendarEl.removeEventListener('touchend', handleTouchEnd);
            calendarEl.removeEventListener('touchcancel', handleTouchEnd);
            // Cleanup timer on unmount
            if (touchTimerRef.current) {
                clearTimeout(touchTimerRef.current);
            }
        };
    }, [isTouchDevice, handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Visual feedback: highlight cells during touch selection
    useEffect(() => {
        if (!isTouchSelecting || !touchStartCell || !touchCurrentCell) {
            // Remove highlight from all cells
            document.querySelectorAll('.fc-daygrid-day.touch-selecting').forEach(cell => {
                cell.classList.remove('touch-selecting');
            });
            return;
        }
        
        const start = new Date(touchStartCell);
        const end = new Date(touchCurrentCell);
        const actualStart = start <= end ? start : end;
        const actualEnd = start <= end ? end : start;
        
        document.querySelectorAll('.fc-daygrid-day:not(.fc-day-other)').forEach(cell => {
            const dateStr = cell.getAttribute('data-date');
            if (!dateStr) return;
            
            const cellDate = new Date(dateStr);
            if (cellDate >= actualStart && cellDate <= actualEnd) {
                cell.classList.add('touch-selecting');
            } else {
                cell.classList.remove('touch-selecting');
            }
        });
    }, [isTouchSelecting, touchStartCell, touchCurrentCell]);



    return(
        <div className="calendar" data-theme={isDarkMode ? 'dark' : 'light'} data-columns={monthColumns} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Month Column Selector - Desktop only (>1000px) */}
            {isDesktop && (
            <Box
                className="month-row-selector"
                sx={{
                    position: 'absolute',
                    top: 18,
                    right: 24,
                    zIndex: 1001,
                }}
            >
                <Tooltip title="Monatszeilen ändern">
                    <IconButton
                        onClick={(e) => setRowSelectorAnchor(e.currentTarget)}
                        sx={{
                            backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
                            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                            boxShadow: isDarkMode ? '0 2px 6px rgba(0, 0, 0, 0.2)' : '0 2px 6px rgba(0, 0, 0, 0.06)',
                            borderRadius: '12px',
                            width: 44,
                            height: 44,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: isDarkMode 
                                    ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' 
                                    : 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
                                borderColor: 'transparent',
                                boxShadow: isDarkMode 
                                    ? '0 4px 12px rgba(255, 152, 0, 0.3)' 
                                    : '0 4px 12px rgba(30, 136, 229, 0.3)',
                                '& svg': {
                                    color: '#fff',
                                },
                            },
                        }}
                    >
                        <GridViewIcon sx={{ 
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#546e7a',
                            fontSize: '1.4rem',
                            transition: 'color 0.2s ease',
                        }} />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={rowSelectorAnchor}
                    open={Boolean(rowSelectorAnchor)}
                    onClose={() => setRowSelectorAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            borderRadius: '12px',
                            minWidth: 140,
                            backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
                            boxShadow: isDarkMode 
                                ? '0 4px 20px rgba(0, 0, 0, 0.4)' 
                                : '0 4px 20px rgba(0, 0, 0, 0.12)',
                        }
                    }}
                >
                    {[2, 3, 4].map((count) => (
                        <MenuItem
                            key={count}
                            onClick={() => handleColumnCountChange(count)}
                            selected={monthColumnCount === count}
                            sx={{
                                py: 1.5,
                                px: 2,
                                fontSize: '0.95rem',
                                color: isDarkMode ? '#fff' : '#333',
                                '&.Mui-selected': {
                                    backgroundColor: isDarkMode 
                                        ? 'rgba(255, 152, 0, 0.15)' 
                                        : 'rgba(30, 136, 229, 0.1)',
                                    color: isDarkMode ? '#ff9800' : '#1976d2',
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    backgroundColor: isDarkMode 
                                        ? 'rgba(255, 152, 0, 0.1)' 
                                        : 'rgba(30, 136, 229, 0.08)',
                                },
                            }}
                        >
                            {count} Monate
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
            )}

            <FullCalendar
                key={`calendar-cols-${monthColumns}`}
                ref={calendarRef}
                plugins={[ multiMonthPlugin, interactionPlugin, timeGridPlugin ]}
                initialView="multiMonthYear"
                locale="de"
                events={calendarEvents}
                firstDay={1}
                headerToolbar={{ left: '', center: 'prev title next', right: '' }}
                initialDate={`${currentYear}-01-01`}                
                multiMonthMaxColumns={monthColumns}                
                height={isMobile ? "auto" : "100%"}
                selectable={true}
                selectAllow={selectAllow}
                select={handleRangeSelect}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                dayCellClassNames={renderDayCellClassNames}
                eventDidMount={handleEventDidMount}
                eventDisplay="none"
                multiMonthTitleFormat={{ year: 'numeric', month: 'short' }}
                dayMaxEvents={2}
                showNonCurrentDates={true}
                fixedWeekCount={false}
                datesSet={handleDatesSet}
            />

            <Dialog open={ showPlots } onClose={ handleClose } aria-describedby="scroll-dialog-description" aria-labelledby="dialog-title"
                maxWidth="lg"
                fullWidth
                fullScreen={isTouchDevice}
                sx={{ 
                    "& .MuiDialog-container": {
                        "& .MuiPaper-root": { 
                            width: "100%", 
                            maxWidth: isTouchDevice ? "100%" : "1400px",
                            height: isTouchDevice ? "100%" : "70vh",
                            maxHeight: isTouchDevice ? "100%" : "70vh",
                            backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
                            color: isDarkMode ? '#ffffff' : '#333333',
                            borderRadius: isTouchDevice ? 0 : 3,
                            display: 'flex',
                            flexDirection: 'column',
                        }
                    },
                }}>
                <DialogTitle 
                    sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                        backgroundColor: isDarkMode ? '#242424' : '#fafafa',
                        color: isDarkMode ? '#ffffff' : '#333333',
                        py: 1.5,
                        px: 2,
                    }} 
                    id="dialog-title">
                    <span>Trainingsverlauf { selectedAthlete?.profile.firstName } { selectedAthlete?.profile.lastName } [{ selectedAthlete?.squad }]</span>
                    <IconButton 
                        aria-label="close" 
                        onClick={handleClose}
                        sx={{ color: isDarkMode ? '#b0b0b0' : '#666' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isTouchDevice ? 'column' : { xs: 'column', md: 'row' }, 
                    overflow: 'hidden', 
                    flex: 1,
                    minHeight: 0, // Important for flex scroll
                }}>
                    {/* Sidebar */}
                    <Box sx={{ 
                        width: isTouchDevice ? '100%' : { xs: '100%', md: '200px' },
                        flexShrink: 0,
                        borderRight: isTouchDevice ? 'none' : { xs: 'none', md: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' },
                        borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                        backgroundColor: isDarkMode ? '#242424' : '#fafafa',
                        p: 1.5,
                        display: 'flex',
                        flexDirection: isTouchDevice ? 'row' : 'column',
                        flexWrap: isTouchDevice ? 'wrap' : 'nowrap',
                        gap: 1,
                        overflow: 'visible',
                    }}>
                        {[focus.Kraft, focus.Ausdauer, focus.Technik, focus.Taktik, focus.Koordination, focus.Schnelligkeit].map((foc) => {
                             const isSelected = !plotPieChart && plotCurrentFocus === foc;
                             const accentColor = isDarkMode ? '#ff9800' : '#1976d2'; // Unified color
                             const label = formatTrainingFocus(foc + 1);
                             const exhaustionColor = plotExhaustionColors[foc];
                             const hasExhaustion = exhaustionColor && exhaustionColor !== 'rgba(0,0,0,0)' && exhaustionColor !== 'transparent';
                             
                             return (
                                <Box 
                                    key={foc}
                                    onClick={() => onClickPlotButton(foc)}
                                    sx={{
                                        flex: isTouchDevice ? 'none' : { xs: 'none', md: 1 },
                                        width: isTouchDevice ? 'auto' : '100%',
                                        boxSizing: 'border-box',
                                        minHeight: isTouchDevice ? '36px' : { xs: '40px', md: 'auto' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: isTouchDevice ? 1.5 : 2,
                                        py: isTouchDevice ? 0.5 : 1,
                                        cursor: 'pointer',
                                        backgroundColor: isSelected 
                                            ? (isDarkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(25, 118, 210, 0.1)')
                                            : 'transparent',
                                        color: isSelected ? accentColor : (isDarkMode ? '#b0b0b0' : '#666666'),
                                        borderRadius: '10px',
                                        borderLeft: isSelected ? `4px solid ${accentColor}` : '4px solid transparent',
                                        fontWeight: isSelected ? 600 : 500,
                                        fontSize: isTouchDevice ? '0.85rem' : '0.95rem',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                                            color: accentColor,
                                        },
                                    }}
                                >
                                    <span>{label}</span>
                                    {hasExhaustion && (
                                        <Box 
                                            sx={{ 
                                                width: 10, 
                                                height: 10, 
                                                borderRadius: '50%', 
                                                backgroundColor: exhaustionColor,
                                                ml: 1,
                                                flexShrink: 0,
                                            }} 
                                        />
                                    )}
                                </Box>
                             );
                        })}
                        <Box 
                            onClick={() => onClickPieChartButton()}
                            sx={{
                                flex: isTouchDevice ? 'none' : { xs: 'none', md: 1 },
                                width: isTouchDevice ? 'auto' : '100%',
                                boxSizing: 'border-box',
                                minHeight: isTouchDevice ? '36px' : { xs: '40px', md: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                px: isTouchDevice ? 1.5 : 2,
                                py: isTouchDevice ? 0.5 : 1,
                                cursor: 'pointer',
                                backgroundColor: plotPieChart 
                                    ? (isDarkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(25, 118, 210, 0.1)')
                                    : 'transparent',
                                color: plotPieChart 
                                    ? (isDarkMode ? '#ff9800' : '#1976d2')
                                    : (isDarkMode ? '#b0b0b0' : '#666666'),
                                borderRadius: '10px',
                                borderLeft: plotPieChart 
                                    ? `4px solid ${isDarkMode ? '#ff9800' : '#1976d2'}` 
                                    : '4px solid transparent',
                                fontWeight: plotPieChart ? 600 : 500,
                                fontSize: isTouchDevice ? '0.85rem' : '0.95rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                                    color: isDarkMode ? '#ff9800' : '#1976d2',
                                },
                            }}
                        >
                            Alle
                        </Box>
                    </Box>
                    {/* Main Content */}
                    <DialogContent 
                        dividers={ false }
                        sx={{
                            flex: 1,
                            backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
                            padding: isTouchDevice ? 1.5 : { xs: 1, sm: 2, md: 3 },
                            overflow: 'auto',
                            minHeight: 0, // Allow proper flex scrolling
                        }}
                    >
                       { !plotPieChart && (
                         <>
                           {/* Custom Legend */}
                           <Stack 
                               direction="row" 
                               spacing={1} 
                               sx={{ 
                                   mb: 2, 
                                   flexWrap: 'wrap', 
                                   gap: 0.5,
                                   justifyContent: 'center',
                               }}
                           >
                               {/* Dauer */}
                               <Chip
                                   label="Dauer"
                                   deleteIcon={<KeyboardArrowDown />}
                                   onDelete={(e) => handleLegendMenuOpen('dauer', e)}
                                   onClick={(e) => handleLegendMenuOpen('dauer', e)}
                                   sx={{ 
                                       minWidth: 110,
                                       height: 32,
                                       borderRadius: '8px',
                                       backgroundColor: (legendVisibility.dauer || legendVisibility.dauerKum) ? '#42a5f5' : 'transparent',
                                       color: (legendVisibility.dauer || legendVisibility.dauerKum) ? '#fff' : (isDarkMode ? '#888' : '#666'),
                                       border: '1px solid #42a5f5',
                                       '& .MuiChip-deleteIcon': { color: 'inherit' }
                                   }}
                               />
                               <Menu
                                   anchorEl={legendMenuAnchor.dauer}
                                   open={Boolean(legendMenuAnchor.dauer)}
                                   onClose={() => handleLegendMenuClose('dauer')}
                               >
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.dauer} onChange={() => toggleLegendItem('dauer')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#42a5f5', opacity: 0.7 }} /><span>Dauer</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.dauerKum} onChange={() => toggleLegendItem('dauerKum')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 3, bgcolor: '#42a5f5' }} /><span>Dauer (kum.)</span></Stack>}
                                       />
                                   </MenuItem>
                               </Menu>

                               {/* Intensität */}
                               <Chip
                                   label="Intensität"
                                   deleteIcon={<KeyboardArrowDown />}
                                   onDelete={(e) => handleLegendMenuOpen('intensitaet', e)}
                                   onClick={(e) => handleLegendMenuOpen('intensitaet', e)}
                                   sx={{ 
                                       minWidth: 110,
                                       height: 32,
                                       borderRadius: '8px',
                                       backgroundColor: (legendVisibility.intensitaet || legendVisibility.intensitaetKum) ? '#ef5350' : 'transparent',
                                       color: (legendVisibility.intensitaet || legendVisibility.intensitaetKum) ? '#fff' : (isDarkMode ? '#888' : '#666'),
                                       border: '1px solid #ef5350',
                                       '& .MuiChip-deleteIcon': { color: 'inherit' }
                                   }}
                               />
                               <Menu
                                   anchorEl={legendMenuAnchor.intensitaet}
                                   open={Boolean(legendMenuAnchor.intensitaet)}
                                   onClose={() => handleLegendMenuClose('intensitaet')}
                               >
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.intensitaet} onChange={() => toggleLegendItem('intensitaet')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#ef5350', opacity: 0.7 }} /><span>Intensität</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.intensitaetKum} onChange={() => toggleLegendItem('intensitaetKum')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 3, bgcolor: '#ef5350' }} /><span>Intensität (kum.)</span></Stack>}
                                       />
                                   </MenuItem>
                               </Menu>

                               {/* Rückmeldung */}
                               <Chip
                                   label="Rückmeldung"
                                   deleteIcon={<KeyboardArrowDown />}
                                   onDelete={(e) => handleLegendMenuOpen('rueckmeldung', e)}
                                   onClick={(e) => handleLegendMenuOpen('rueckmeldung', e)}
                                   sx={{ 
                                       minWidth: 110,
                                       height: 32,
                                       borderRadius: '8px',
                                       backgroundColor: (legendVisibility.rueckmeldungDauer || legendVisibility.rueckmeldungIntensitaet) ? '#8d6e63' : 'transparent',
                                       color: (legendVisibility.rueckmeldungDauer || legendVisibility.rueckmeldungIntensitaet) ? '#fff' : (isDarkMode ? '#888' : '#666'),
                                       border: '1px solid #8d6e63',
                                       '& .MuiChip-deleteIcon': { color: 'inherit' }
                                   }}
                               />
                               <Menu
                                   anchorEl={legendMenuAnchor.rueckmeldung}
                                   open={Boolean(legendMenuAnchor.rueckmeldung)}
                                   onClose={() => handleLegendMenuClose('rueckmeldung')}
                               >
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.rueckmeldungDauer} onChange={() => toggleLegendItem('rueckmeldungDauer')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#a1887f' }} /><span>Dauer</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.rueckmeldungIntensitaet} onChange={() => toggleLegendItem('rueckmeldungIntensitaet')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#6d4c41' }} /><span>Intensität</span></Stack>}
                                       />
                                   </MenuItem>
                               </Menu>

                               {/* Load */}
                               <Chip
                                   label="Load"
                                   deleteIcon={<KeyboardArrowDown />}
                                   onDelete={(e) => handleLegendMenuOpen('load', e)}
                                   onClick={(e) => handleLegendMenuOpen('load', e)}
                                   sx={{ 
                                       minWidth: 110,
                                       height: 32,
                                       borderRadius: '8px',
                                       backgroundColor: (legendVisibility.load || legendVisibility.loadKum || legendVisibility.rueckmeldungLoad || legendVisibility.trainingLoadKum) ? '#9c27b0' : 'transparent',
                                       color: (legendVisibility.load || legendVisibility.loadKum || legendVisibility.rueckmeldungLoad || legendVisibility.trainingLoadKum) ? '#fff' : (isDarkMode ? '#888' : '#666'),
                                       border: '1px solid #9c27b0',
                                       '& .MuiChip-deleteIcon': { color: 'inherit' }
                                   }}
                               />
                               <Menu
                                   anchorEl={legendMenuAnchor.load}
                                   open={Boolean(legendMenuAnchor.load)}
                                   onClose={() => handleLegendMenuClose('load')}
                               >
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.load} onChange={() => toggleLegendItem('load')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#9c27b0', opacity: 0.4 }} /><span>Load</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.loadKum} onChange={() => toggleLegendItem('loadKum')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 2, bgcolor: '#9c27b0', borderStyle: 'dotted' }} /><span>Load (kum.)</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.rueckmeldungLoad} onChange={() => toggleLegendItem('rueckmeldungLoad')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 12, bgcolor: '#d7ccc8' }} /><span>Rückmeldung</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.trainingLoadKum} onChange={() => toggleLegendItem('trainingLoadKum')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 16, height: 2, bgcolor: '#d7ccc8', borderStyle: 'dotted' }} /><span>Training (kum.)</span></Stack>}
                                       />
                                   </MenuItem>
                               </Menu>

                               {/* Erschöpfung */}
                               <Chip
                                   label="Erschöpfung"
                                   deleteIcon={<KeyboardArrowDown />}
                                   onDelete={(e) => handleLegendMenuOpen('erschoepfung', e)}
                                   onClick={(e) => handleLegendMenuOpen('erschoepfung', e)}
                                   sx={{ 
                                       minWidth: 110,
                                       height: 32,
                                       borderRadius: '8px',
                                       backgroundColor: (legendVisibility.physischeErschoepfung || legendVisibility.mentaleErschoepfung) ? '#388e3c' : 'transparent',
                                       color: (legendVisibility.physischeErschoepfung || legendVisibility.mentaleErschoepfung) ? '#fff' : (isDarkMode ? '#888' : '#666'),
                                       border: '1px solid #388e3c',
                                       '& .MuiChip-deleteIcon': { color: 'inherit' }
                                   }}
                               />
                               <Menu
                                   anchorEl={legendMenuAnchor.erschoepfung}
                                   open={Boolean(legendMenuAnchor.erschoepfung)}
                                   onClose={() => handleLegendMenuClose('erschoepfung')}
                               >
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.physischeErschoepfung} onChange={() => toggleLegendItem('physischeErschoepfung')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#388e3c' }} /><span>Physisch</span></Stack>}
                                       />
                                   </MenuItem>
                                   <MenuItem dense>
                                       <FormControlLabel 
                                           control={<Checkbox size="small" checked={legendVisibility.mentaleErschoepfung} onChange={() => toggleLegendItem('mentaleErschoepfung')} />}
                                           label={<Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef5350' }} /><span>Mental</span></Stack>}
                                       />
                                   </MenuItem>
                               </Menu>
                           </Stack>
                           
                         <Suspense fallback={<div>Diagramm wird geladen...</div>}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <Plot 
                                    data={ plotData }
                                    layout={ plotLayout }
                                    style={{ width: '100%', maxWidth: '100%' }}
                                    useResizeHandler={true}
                                    config={{ displaylogo: false, responsive: true }}
                                    revision={ plotRevision }
                                    onUpdate={ (e) => onXaxisZoomPan(e) }/>
                            </Box>
                        </Suspense>
                         </>
                        )}
                        { plotPieChart && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <div style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: 500, marginBottom: '8px', color: isDarkMode ? '#ffffff' : '#333333' }}>  
                                        { plotPieBarChartTitle }   
                                    </div>
                                </Grid> 
                                <Grid item xs={12} md={6}>
                                    <Suspense fallback={<div>Diagramm wird geladen...</div>}>
                                        <Plot
                                            data={ plotDataPieChart }
                                            style={{ width: '100%' }}
                                            useResizeHandler={true}
                                            config={ { displaylogo: false, responsive: true } } 
                                            layout={ plotLayoutPieChart}/>   
                                    </Suspense> 
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Suspense fallback={<div>Diagramm wird geladen...</div>}>
                                        <Plot
                                            data={ [plotDataBarChart, plotDataBarChart2] }
                                            style={{ width: '100%' }}
                                            useResizeHandler={true}
                                            config={ { displaylogo: false, responsive: true } } 
                                            layout={ plotLayoutBarChart }/> 
                                    </Suspense>
                                </Grid>
                            </Grid>
                        )}
                    </DialogContent>
                </Box>
            </Dialog>
        </div>
    );
}

export default YearCalendar;